import { Link } from "react-router-dom";
import { Shield, Scale, AlertTriangle, FileText, ArrowRight, Lock, UserCheck, Ban, Gavel, Zap, Target, Handshake } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { RelatedLinks } from "@/components/RelatedLinks";

import criminalDefenseHero from "@/assets/criminal-defense-hero.jpg";

const CriminalDefense = () => {
  const { language } = useLanguage();

  const copy = {
    title: { en: "Criminal Defense", es: "Defensa Penal", vi: "Bào Chữa Hình Sự" },
    subtitle: {
      en: "Aggressive, experienced criminal defense representation. We fight to protect your rights, your freedom, and your future.",
      es: "Representación penal agresiva y experimentada. Protegemos sus derechos, su libertad y su futuro.",
      vi: "Bào chữa hình sự mạnh mẽ và dày dạn. Bảo vệ quyền lợi, tự do và tương lai của bạn.",
    },
    schedule: { en: "Schedule Consultation", es: "Agende Consulta", vi: "Đặt Lịch Tư Vấn" },
    call: { en: "Call:", es: "Llamar:", vi: "Gọi:" },
    callNow: { en: "Call Now:", es: "Llamar Ahora:", vi: "Gọi Ngay:" },
    ourServices: { en: "Our Criminal Defense Services", es: "Nuestros Servicios de Defensa Penal", vi: "Dịch Vụ Bào Chữa Hình Sự" },
    ourServicesDesc: {
      en: "Comprehensive defense representation for all criminal charges in Texas",
      es: "Representación integral para todos los cargos penales en Texas",
      vi: "Đại diện bào chữa toàn diện cho mọi tội danh tại Texas",
    },
    learnMore: { en: "Learn More", es: "Ver Más", vi: "Tìm Hiểu" },
    knowRights: { en: "Know Your Rights", es: "Conozca Sus Derechos", vi: "Biết Quyền Của Bạn" },
    rights: [
      { title: { en: "Right to Remain Silent", es: "Derecho a Guardar Silencio", vi: "Quyền Im Lặng" }, desc: { en: "You are not required to answer police questions. Invoke your right to remain silent and request an attorney immediately.", es: "No está obligado a responder preguntas de la policía. Invoque su derecho a guardar silencio y solicite un abogado de inmediato.", vi: "Bạn không bắt buộc phải trả lời câu hỏi của cảnh sát. Hãy yêu cầu được im lặng và có luật sư." }, icon: Lock },
      { title: { en: "Right to an Attorney", es: "Derecho a un Abogado", vi: "Quyền Có Luật Sư" }, desc: { en: "You have the right to legal representation. Don't make any statements or decisions without consulting your lawyer first.", es: "Tiene derecho a representación legal. No haga declaraciones ni decisiones sin consultar a su abogado.", vi: "Bạn có quyền được đại diện pháp lý. Đừng đưa ra quyết định nếu chưa tham vấn luật sư." }, icon: Scale },
      { title: { en: "Protection from Illegal Search", es: "Protección contra Búsquedas Ilegales", vi: "Bảo Vệ Khỏi Khám Xét Trái Pháp Luật" }, desc: { en: "Police need a warrant or probable cause to search you, your car, or your home. You can refuse consent to search.", es: "La policía necesita una orden o causa probable para registrar. Puede negarse a dar consentimiento.", vi: "Cảnh sát cần lệnh khám hoặc lý do hợp pháp. Bạn có thể từ chối đồng ý khám xét." }, icon: Ban },
      { title: { en: "Presumption of Innocence", es: "Presunción de Inocencia", vi: "Suy Đoán Vô Tội" }, desc: { en: "You are innocent until proven guilty. The prosecution must prove every element of their case beyond a reasonable doubt.", es: "Usted es inocente hasta que se pruebe lo contrario. La fiscalía debe probar su caso más allá de toda duda razonable.", vi: "Bạn vô tội cho đến khi bị chứng minh có tội. Công tố phải chứng minh vượt qua mọi nghi ngờ hợp lý." }, icon: Gavel },
    ],
    faqTitle: { en: "Frequently Asked Questions", es: "Preguntas Frecuentes", vi: "Câu Hỏi Thường Gặp" },
    faqs: [
      { q: { en: "Should I talk to the police without a lawyer?", es: "¿Debo hablar con la policía sin un abogado?", vi: "Có nên nói chuyện với cảnh sát khi không có luật sư?" }, a: { en: "No. You have the constitutional right to remain silent and to have an attorney present during questioning.", es: "No. Tiene derecho constitucional a guardar silencio y tener un abogado presente durante el interrogatorio.", vi: "Không. Bạn có quyền hiến định được im lặng và có luật sư khi thẩm vấn." } },
      { q: { en: "What happens at my first court appearance?", es: "¿Qué pasa en mi primera audiencia?", vi: "Điều gì xảy ra tại phiên tòa đầu tiên?" }, a: { en: "Your first appearance is typically the arraignment... Do not plead guilty.", es: "La primera audiencia suele ser la instrucción de cargos... No se declare culpable.", vi: "Phiên đầu tiên thường là buộc tội... Đừng nhận tội." } },
      { q: { en: "Can my criminal charges be dismissed?", es: "¿Pueden desestimarse mis cargos?", vi: "Cáo buộc có thể bị bác bỏ không?" }, a: { en: "Possibly. Charges can be dismissed for many reasons...", es: "Posiblemente. Los cargos pueden desestimarse por muchas razones...", vi: "Có thể. Cáo buộc có thể bị bác bỏ vì nhiều lý do..." } },
      { q: { en: "What's the difference between a misdemeanor and a felony?", es: "¿Diferencia entre delito menor y grave?", vi: "Khác nhau giữa tội nhẹ và trọng tội?" }, a: { en: "Misdemeanors are less serious...", es: "Los delitos menores son menos graves...", vi: "Tội nhẹ ít nghiêm trọng hơn..." } },
      { q: { en: "Will I go to jail?", es: "¿Iré a la cárcel?", vi: "Tôi có vào tù không?" }, a: { en: "Not necessarily. Many factors affect sentencing...", es: "No necesariamente. Muchos factores influyen en la sentencia...", vi: "Chưa chắc. Nhiều yếu tố ảnh hưởng mức án..." } },
    ],
    whyTitle: { en: "Why Choose Our Criminal Defense Team?", es: "¿Por Qué Elegir Nuestro Equipo Penal?", vi: "Vì Sao Chọn Đội Ngũ Bào Chữa Của Chúng Tôi?" },
    why: [
      { title: { en: "Prompt Response", es: "Respuesta Inmediata", vi: "Phản Hồi Ngay" }, desc: { en: "Quick response during business hours for arrests and urgent matters.", es: "Respuesta rápida durante el horario de oficina para arrestos y asuntos urgentes.", vi: "Phản hồi nhanh trong giờ làm việc cho tình huống khẩn cấp." } },
      { title: { en: "Aggressive Defense", es: "Defensa Agresiva", vi: "Bào Chữa Quyết Liệt" }, desc: { en: "Thorough investigation, qualified witnesses, and courtroom experience.", es: "Investigación exhaustiva, peritos y experiencia en juicio.", vi: "Điều tra kỹ lưỡng, chuyên gia và kinh nghiệm tranh tụng." } },
      { title: { en: "Personal Attention", es: "Atención Personal", vi: "Quan Tâm Cá Nhân" }, desc: { en: "Direct access to your attorney. We keep you informed.", es: "Acceso directo a su abogado. Le mantenemos informado.", vi: "Liên hệ trực tiếp với luật sư. Luôn cập nhật cho bạn." } },
    ],
  } as const;

  const whyIcons = [Zap, Target, Handshake];

  const services = [
    { icon: Shield, title: { en: "DWI/DUI Defense", es: "Defensa DWI/DUI", vi: "Bào Chữa DWI/DUI" }, desc: { en: "Aggressive defense against drunk driving charges...", es: "Defensa agresiva contra cargos por conducir ebrio...", vi: "Bào chữa mạnh mẽ với cáo buộc lái xe say..." }, href: "/practice-areas/criminal-defense/dwi" },
    { icon: AlertTriangle, title: { en: "Drug Charges", es: "Delitos de Drogas", vi: "Cáo Buộc Ma Túy" }, desc: { en: "Defense for possession, distribution, and trafficking charges.", es: "Defensa por posesión, distribución y tráfico.", vi: "Bào chữa tội tàng trữ, phân phối, buôn bán." }, href: "/practice-areas/criminal-defense/drug-charges" },
    { icon: Scale, title: { en: "Assault & Family Violence", es: "Agresión y Violencia Familiar", vi: "Hành Hung & Bạo Lực Gia Đình" }, desc: { en: "Defense against assault and domestic violence charges.", es: "Defensa ante cargos de agresión y violencia doméstica.", vi: "Bào chữa cho cáo buộc hành hung, bạo lực gia đình." }, href: "/practice-areas/criminal-defense/assault-family-violence" },
    { icon: Shield, title: { en: "Violent Crimes", es: "Delitos Violentos", vi: "Tội Bạo Lực" }, desc: { en: "Defense for serious charges including robbery and aggravated assault.", es: "Defensa para cargos graves como robo y asalto agravado.", vi: "Bào chữa cho các tội nghiêm trọng như cướp, hành hung nghiêm trọng." }, href: "/practice-areas/criminal-defense/violent-crimes" },
    { icon: FileText, title: { en: "Sex Crimes Defense", es: "Defensa en Delitos Sexuales", vi: "Bào Chữa Tội Phạm Tình Dục" }, desc: { en: "Sensitive, aggressive defense for sex-related charges.", es: "Defensa sensible y enérgica para cargos sexuales.", vi: "Bào chữa nhạy cảm và quyết liệt cho cáo buộc tình dục." }, href: "/practice-areas/criminal-defense/sex-crimes" },
    { icon: Scale, title: { en: "Theft & Robbery", es: "Robo y Hurto", vi: "Trộm Cắp & Cướp" }, desc: { en: "Defense for shoplifting, burglary, robbery, and theft.", es: "Defensa por hurto, allanamiento, robo y otros.", vi: "Bào chữa tội trộm cắp, đột nhập, cướp..." }, href: "/practice-areas/criminal-defense/theft-robbery" },
  ];

  const localizedServices = services.map((s) => ({
    icon: s.icon,
    title: s.title[language],
    description: s.desc[language],
    href: s.href,
  }));

  const faqs = copy.faqs.map((f) => ({ question: f.q[language], answer: f.a[language] }));

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${criminalDefenseHero})` }}>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/98 via-primary/95 to-primary-dark/98"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-primary-foreground">
            <Shield className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6 drop-shadow-lg">{copy.title[language]}</h1>
            <p className="text-xl opacity-95 mb-8 drop-shadow-md">{copy.subtitle[language]}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="hero">
                <LocalizedLink to="/appointment">{copy.schedule[language]}</LocalizedLink>
              </Button>
              <a href="tel:8329303059">
                <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary-dark bg-white/10 backdrop-blur-sm">
                  {copy.call[language]} (832) 930-3059
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>


      {/* Services Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">{copy.ourServices[language]}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{copy.ourServicesDesc[language]}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {localizedServices.map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-accent" />
                  </div>
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription>{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="link" className="p-0">
                    <LocalizedLink to={service.href}>
                      {copy.learnMore[language]} <ArrowRight className="ml-1 h-4 w-4" />
                    </LocalizedLink>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional services simplified into cards if needed */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="text-xl">Federal Crimes</CardTitle>
                <CardDescription>Defense against federal criminal charges.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="link" className="p-0">
                  <LocalizedLink to="/practice-areas/criminal-defense/federal-crimes">
                    Learn More <ArrowRight className="ml-1 h-4 w-4" />
                  </LocalizedLink>
                </Button>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  <UserCheck className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="text-xl">Expunctions & Non-Disclosure</CardTitle>
                <CardDescription>Clearing your criminal record.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="link" className="p-0">
                  <LocalizedLink to="/practice-areas/criminal-defense/expunctions-non-disclosure">
                    Learn More <ArrowRight className="ml-1 h-4 w-4" />
                  </LocalizedLink>
                </Button>
              </CardContent>
            </Card>
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-accent" />
                </div>
                <CardTitle className="text-xl">Appeals & Post-Conviction</CardTitle>
                <CardDescription>Appealing wrongful convictions.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="link" className="p-0">
                  <LocalizedLink to="/practice-areas/criminal-defense/appeals-post-conviction">
                    Learn More <ArrowRight className="ml-1 h-4 w-4" />
                  </LocalizedLink>
                </Button>
              </CardContent>
            </Card>
          </div> */}
        </div>
      </section>

      {/* Your Rights Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{copy.knowRights[language]}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {copy.rights.map((r, idx) => {
                const IconComponent = r.icon;
                return (
                  <div key={idx} className="bg-card p-6 rounded-lg shadow-md">
                    <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mb-4 shadow-gold">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{r.title[language]}</h3>
                    <p className="text-muted-foreground">{r.desc[language]}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">{copy.faqTitle[language]}</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">{copy.whyTitle[language]}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {copy.why.map((w, i) => {
                const IconComponent = whyIcons[i];
                return (
                  <div key={i}>
                    <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                      <IconComponent className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{w.title[language]}</h3>
                    <p className="text-sm text-muted-foreground">{w.desc[language]}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <RelatedLinks category="criminal" />

      <CTASection
        title={{ en: "Facing Criminal Charges? Don't Wait.", es: "¿Enfrenta Cargos Penales? No Espere.", vi: "Đang Bị Truy Tố? Đừng Chần Chờ." }[language]}
        description={{ en: "Every minute counts in a criminal case. Contact Daniel Albert Law Firm now for aggressive defense representation.", es: "Cada minuto cuenta en un caso penal. Contáctenos ahora para una defensa agresiva.", vi: "Mỗi phút đều quan trọng. Liên hệ ngay để được bào chữa mạnh mẽ." }[language]}
      />

      <Footer />
    </div>
  );
};

export default CriminalDefense;
